import React, { useEffect, useState } from 'react';
import { Button, Modal, Row, Col } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { loadCompanyAssetsStart } from 'services/redux/actions/serviceDesk';
import { showSuccessfulToast } from './Toasts/ImportLinkToasts/showSuccessfulToast';
import { showFailedToast } from './Toasts/ImportLinkToasts/showFailedToast';
import { getCompanyIdFromRoute } from 'services/redux/selectors/company';

import conxtdOut from 'apis/conxtdOut';
import Axios from '../../../../../../../node_modules/axios/index';

import Loading from "components/Loading";
import Alert from "components/Common/Alert";
import Progress from "components/Common/EnterpriseManager/Progress";

import "./AssetImportExport.scss"

function BulkDeleteAssets({ 
  showDeleteModal, 
  setShowDeleteModal, 
  setShowDeleteStatus,
  submittingToast,
  }) {

  const dispatch = useDispatch();
  const history = useHistory();

  const companyId = useSelector(getCompanyIdFromRoute);
  const darkMode = useSelector(state => state.app.darkMode)

  const [uploadingDeleteFile, setUploadingDeleteFile] = useState(false);
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [uploadPercentage, setUploadPercentage] = useState(0);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  
  const initialiseState = () => {
    setTimeout(() => {
      setFile(null);
      setFileName("");
      setUploadPercentage(0);
      setUploadingDeleteFile(false);
    }, [500])
  }

  const hideModal = () => {
    setShowDeleteModal(false);
  }

  useEffect(() => {
      return () => initialiseState();
  }, [companyId, showDeleteModal])

  useEffect(() => {
    if (uploadingDeleteFile) {
      submittingToast("Preparing File");
    }
  }, [uploadingDeleteFile])

  // confirm choice --------------------------->
  useEffect(() => {
    if (showDeleteAlert) {
      Alert({
        text: "Are you sure you want to delete the assets you have uploaded?",
        icon: "warning",
        confirmButtonText: "Delete Assets",
        showCancelButton: true,
        showConfirmButton: true,
      })
      .then((result) => {
        if (result.value) {
          deleteAssets();
        } else {
          setShowDeleteAlert(false);
          return;
        }
      })
      .catch((error) => {
        console.log(error)
      });
    }
  }, [showDeleteAlert])
  // -------------------------------------------|

  const deleteAssets = () => {
    if (!companyId) return;
    setUploadingDeleteFile(true);
    setShowDeleteStatus(true);

    const source = Axios.CancelToken.source();
    const formData = new FormData();
    formData.append("file", file);
    
    const submit = conxtdOut.post(`/company-assets-bulk/bulk-delete/${companyId}`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      cancelToken: source.token,
      onUploadProgress: (progressEvent) => {
        const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadPercentage(percentage);
        setTimeout(() => setUploadPercentage(0), 1500);
      },
    })

      hideModal();

      submit.then((response) => {
        // calls successful toast with link to progress logs
        showSuccessfulToast({
          fileName,
          title: "File Received Successfully",
          scheme: "SCHEME_GREEN",
          message: response?.message ? "168 Assets have been removed" : "",
          large: true,
          history,
          companyId,
        });

        // --------------------------------------|

        setUploadingDeleteFile(false);
        setShowDeleteStatus(false);
        setShowDeleteAlert(false);
        dispatch(loadCompanyAssetsStart({ companyId }));
      })
      .catch((error) => {
        setUploadingDeleteFile(false);
        setShowDeleteStatus(false);
        setShowDeleteAlert(false);
        console.error("Error on importing sites", error);
        
        const message = error?.message ?? "An error was encountered when retrieving the file";
        // calls failed toast with link to progress logs
        showFailedToast({
          fileName,
          title: "Failed To Receive File",
          scheme: "SCHEME_RED",
          message: message ? message : "",
          large: true,
          history,
          companyId,
        });

      });

    return () => {
      source.cancel("Component unmounted, request cancelled");
    }
  };

  const onFileChange = (e) => {
    setFile(e.target.files[0]);
    setFileName(e.target.files[0]?.name);
  };

  return (
    <>
      <Modal
        id='bulk-import-assets-modal'
        backdrop='static'    
        centered
        keyboard={false}
        onHide={hideModal}
        show={showDeleteModal}
        size='lg'
      >
        <Modal.Header
          closeButton
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <Modal.Title className='w-100'>
            <Row className='d-flex justify-content-between align-items-center'>
              <Col xs="auto" sm="auto" md="auto" lg="auto">
                <span>Import Assets To Be Deleted</span>
              </Col>
            </Row>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          className={`${darkMode ? 'darkMode' : 'background-modal-wrapper'}`}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <div className="template-container">
            <div>
              <div>Load an exported file containing the assets you wish to delete</div>
              <span id='delete-assets-note'>
                <div className='font-italic'>-- Note: assets must have <span className='font-weight-bold'>asset_uid</span> values populated to be successfully detected for deletion</div>
              </span>
            </div>
          
            <div>
              <div id="stage-2-container" className="bulk-assets-input">
                <div className="mt-4">Upload your completed spreadsheet here to delete your assets.</div>
                <div className="custom-file mt-2 mb-2">
                  <input type="file" className="custom-file-input" id="customFile" onChange={onFileChange} />
                  <label className="custom-file-label" htmlFor="customFile">
                    {fileName}
                  </label>
                </div>
                
                <div className="mt-2">
                  {uploadingDeleteFile && 
                  <div className="d-flex flex-row align-items-center mb-3 mt-2">
                    <span className="mr-1">Processing</span>
                    <Loading />
                  </div>
                  }
                  <Button style={{ width: "100%" }} variant="success" onClick={() => setShowDeleteAlert(true)} disabled={uploadingDeleteFile}>
                    Import Delete File
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-end">
            <Button className="mr-2 p-2" variant="primary" onClick={hideModal}>
              Close
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default BulkDeleteAssets;